import React from 'react';
import { useForm } from 'react-hook-form';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import Button from 'src/components/controls/forms/redesign/FormButton';
import Ap1099 from 'src/models/AP/Ap1099';

interface IVendorTaxTypeOption {
    code: string;
    description: string;
}

interface IAp1099GridProps {
    rows: Array<Ap1099>;
    taxTypes: Array<IVendorTaxTypeOption>;
    onSaveRows: (rows: Array<Ap1099>) => void;
    saving?: boolean;
}

interface IAp1099FormValues {
    ap1099: Array<{ checkAmount: string; taxType: string }>;
}

// Ports ap_1099.fmt's LM_1099 block grid (Year/Vndr No./Name/Amount/IRS Tax Classification).
// Vndr No./Name/Year are the row's identity (re-derived by lookup in Oracle, not stored as
// editable text) - only CHK_AMT/TAX_TYPE are actually editable, matching WHAT the form's own
// WHEN-VALIDATE-ITEM trigger touches. Save follows this codebase's standard array-form pattern
// (one useForm() over all rows, per-row dirty tracking via formState.dirtyFields) rather than a
// per-row save button, same shape as CreditCardsRecPage.tsx's editable detail grid.
const Ap1099Grid = (props: IAp1099GridProps) => {
    const { register, errors, handleSubmit, formState } = useForm();
    const { dirtyFields } = formState;

    const onSubmit = (formData: IAp1099FormValues) => {
        const dirtyIndexes = Object.keys(dirtyFields?.ap1099 || {});

        if (dirtyIndexes.length === 0) {
            return;
        }

        const updatedRows = dirtyIndexes.map((indexKey) => {
            const index = Number(indexKey);
            const row = props.rows[index];
            const formRow = formData.ap1099[index];

            return {
                ...row,
                checkAmount: Number(formRow.checkAmount) || 0,
                taxType: formRow.taxType
            };
        });

        props.onSaveRows(updatedRows);
    };

    if (!props.rows || props.rows.length === 0) {
        return <div className="bold-centered" style={{ padding: '1rem' }}>No 1099 records found. Try searching, or Seed a tax year.</div>;
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <table className="dlc-table" style={{ width: '100%' }}>
                <thead>
                    {/* Sticky within the same 30vh scroll container as the tfoot below, so the
                        header stays visible over a long row list. z-index is deliberately low
                        (1) - the search bar's Select menu is portaled to document.body with
                        z-index 9999, and this must never be able to compete with it again. */}
                    <tr>
                        <th style={{ position: 'sticky', top: 0, zIndex: 1, background: '#fff' }}>Year</th>
                        <th style={{ position: 'sticky', top: 0, zIndex: 1, background: '#fff' }}>Vndr No.</th>
                        <th style={{ position: 'sticky', top: 0, zIndex: 1, background: '#fff' }}>Name</th>
                        <th style={{ position: 'sticky', top: 0, zIndex: 1, background: '#fff' }}>Amount</th>
                        <th style={{ position: 'sticky', top: 0, zIndex: 1, background: '#fff' }}>IRS Tax Classification</th>
                    </tr>
                </thead>
                <tbody>
                    {props.rows.map((row, index) => (
                        <tr key={row.apId}>
                            <td>{row.taxYear}</td>
                            <td>{row.vendorNumber}</td>
                            <td>{row.vendorName}</td>
                            <td style={{ width: '15%' }}>
                                <RHFInputBox
                                    name={`ap1099[${index}].checkAmount`}
                                    type="number"
                                    defaultVal={`${row.checkAmount}`}
                                    register={register}
                                    errors={errors}
                                    style={{ width: '100%' }}
                                    bodyStyle={{ width: '100%' }}
                                />
                            </td>
                            <td style={{ width: '20%' }}>
                                <RHFSelect
                                    name={`ap1099[${index}].taxType`}
                                    defaultVal={row.taxType}
                                    options={props.taxTypes?.map((t) => ({ value: t.code, label: t.description }))}
                                    register={register}
                                    errors={errors}
                                    style={{ width: '100%' }}
                                    bodyStyle={{ width: '100%' }}
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        {/* Sticky within the dialog's scrollable 30vh container (Ap1099MaintenanceDialog.tsx),
                            so Save stays reachable without scrolling past a long row list. */}
                        <td colSpan={5} style={{ position: 'sticky', bottom: 0, background: '#fff', borderTop: '1px solid #ccc', padding: '.5rem', textAlign: 'center' }}>
                            <Button
                                text={props.saving ? 'Saving...' : 'Save'}
                                className="blue-hover-button"
                                type="submit"
                                disabled={props.saving}
                                style={{ width: '8rem' }}
                            />
                        </td>
                    </tr>
                </tfoot>
            </table>
        </form>
    );
};

export default Ap1099Grid;



// WEBPACK FOOTER //
// ./src/components/accounts-payable/components/Ap1099Grid.tsx